/**
 * Axios Configuration with Token Refresh Interceptor
 *
 * - Access token is stored in memory and sent in Authorization header
 * - Refresh token is stored in httpOnly cookie (sent automatically by browser)
 * - Automatically refreshes expired access tokens using refresh endpoint
 */

import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { API_ENDPOINTS } from '../config/api.config';
import {
  getToken,
  setToken,
  clearAuthData,
  isTokenExpired,
} from '../utils/auth';

// Create axios instance with credentials support for cookies
const axiosInstance = axios.create({
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests
});

// Flag to prevent multiple simultaneous refresh requests
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

/**
 * Process queued requests after token refresh
 */
const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Refresh access token using httpOnly cookie (refresh token is sent automatically)
 */
export const refreshAccessToken = async (): Promise<string> => {
  console.log('[Token Refresh] Starting token refresh...');

  try {
    console.log(
      '[Token Refresh] Calling refresh endpoint:',
      API_ENDPOINTS.AUTH.REFRESH,
    );

    // Refresh token is sent automatically via cookie
    const response = await axios.post(
      API_ENDPOINTS.AUTH.REFRESH,
      {},
      { withCredentials: true },
    );

    console.log('[Token Refresh] Response received:', {
      status: response.status,
      hasAccessToken: !!response.data?.accessToken,
    });

    const { accessToken } = response.data;

    if (!accessToken) {
      console.error('[Token Refresh] No access token in response');
      throw new Error('No access token in response');
    }

    // Save new access token to memory
    setToken(accessToken);
    console.log('[Token Refresh] Access token updated in memory');

    return accessToken;
  } catch (error) {
    console.error('[Token Refresh] Failed:', error);
    clearAuthData();
    throw error;
  }
};

/**
 * Request Interceptor: Add Authorization header
 */
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = getToken();

    // Skip auth for login endpoint
    if (config.url === API_ENDPOINTS.AUTH.LOGIN) {
      return config;
    }

    // For refresh endpoint, don't add token (cookie will be sent)
    if (config.url === API_ENDPOINTS.AUTH.REFRESH) {
      return config;
    }

    // Check if token is expired and needs refresh
    if (token && isTokenExpired(token)) {
      console.log('[Request Interceptor] Token is expired or expiring soon');

      if (!isRefreshing) {
        try {
          console.log('[Request Interceptor] Starting token refresh...');
          isRefreshing = true;
          const newAccessToken = await refreshAccessToken();
          config.headers.Authorization = `Bearer ${newAccessToken}`;
          processQueue(null, newAccessToken);
          console.log('[Request Interceptor] Request will use new token');
          return config;
        } catch (error) {
          console.error(
            '[Request Interceptor] Refresh failed, redirecting to login',
          );
          processQueue(error as Error, null);
          clearAuthData();
          window.location.href = '/signin';
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      }
    }

    // Add current token to headers
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    console.error('[Request Interceptor] Error:', error);
    return Promise.reject(error);
  },
);

/**
 * Response Interceptor: Handle 401 errors with token refresh
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle 401 Unauthorized
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      console.log(
        '[Response Interceptor] 401 Unauthorized detected for:',
        originalRequest.url,
      );

      // Skip retry for login/refresh endpoints
      if (
        originalRequest.url === API_ENDPOINTS.AUTH.LOGIN ||
        originalRequest.url === API_ENDPOINTS.AUTH.REFRESH
      ) {
        console.log('[Response Interceptor] Skipping retry for auth endpoint');
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        console.log(
          '[Response Interceptor] Already refreshing, queuing request',
        );
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            console.log(
              '[Response Interceptor] Retrying queued request with new token',
            );
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            console.error('[Response Interceptor] Queued request failed:', err);
            return Promise.reject(err);
          });
      }

      // Mark request as retry
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        console.log(
          '[Response Interceptor] Starting token refresh for failed request',
        );
        const newAccessToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        console.log(
          '[Response Interceptor] Retrying original request with new token',
        );
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error('[Response Interceptor] Refresh failed, logging out');
        processQueue(refreshError as Error, null);
        clearAuthData();
        window.location.href = '/signin';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
